import { onMount, onCleanup, For, Show, createMemo, createSignal } from "solid-js";
import { createStore, produce } from "solid-js/store";
import { subscribeInventoryEvents, type InventoryEvent } from "../services/inventoryEvents";
import { useInventoryApi, type CraftingTaskInfo } from "../services/inventoryApi";
import { showDialog } from "./Dialog";
import Text from "./Text";
import { useTranslation } from "../hooks/useTranslation";
import { useItemName } from "../hooks/useItemName";
import { CraftingResolverService } from "../services/craftingResolver";
import ItemStackView from "./ItemStackView";
import { fromItemDef } from "../services/itemRefAdapters";
import { formatDateTime } from "../services/dateFormat";
import styles from "./CraftingTaskPanel.module.css";

interface Props {
  resolver: CraftingResolverService;
}

export default function CraftingTaskPanel(props: Props) {
  const { t } = useTranslation();
  const { itemName } = useItemName();
  const [tasks, setTasks] = createStore<CraftingTaskInfo[]>([]);
  const [open, setOpen] = createSignal(false);
  const inv = useInventoryApi();

  onMount(async () => {
    try {
      const existing = await inv.fetchTasks();
      setTasks(existing);
    } catch {}
  });

  const unsub = subscribeInventoryEvents((evt: InventoryEvent) => {
    switch (evt.type) {
      case "task_created": {
        const p = evt.payload;
        setTasks(produce(prev => {
          prev.unshift({
            id: p.taskId,
            username: p.username,
            taskType: p.taskType ?? "Craft",
            itemKey: p.itemKey,
            quantity: p.quantity,
            sourceStorageId: p.sourceStorageId,
            sourceStorageName: p.sourceStorageName,
            resultStorageId: p.resultStorageId,
            resultStorageName: p.resultStorageName,
            status: "Pending",
            progress: 0,
            createdAt: evt.timestamp,
          });
        }));
        break;
      }
      case "task_progress": {
        const p = evt.payload;
        const idx = tasks.findIndex(t => t.id === p.taskId);
        if (idx >= 0) {
          setTasks(idx, "progress", p.progress);
          setTasks(idx, "status", p.status);
        }
        break;
      }
      case "task_completed": {
        const p = evt.payload;
        const idx = tasks.findIndex(t => t.id === p.taskId);
        if (idx >= 0) {
          setTasks(idx, "status", "Completed");
          setTasks(idx, "progress", 100);
          setTasks(idx, "completedAt", evt.timestamp);
        }
        break;
      }
      case "task_failed": {
        const p = evt.payload;
        const idx = tasks.findIndex(t => t.id === p.taskId);
        if (idx >= 0) {
          setTasks(idx, "status", "Failed");
          setTasks(idx, "progress", 0);
          setTasks(idx, "errorMessage", p.error);
          setTasks(idx, "errorDetail", p.detail);
          setTasks(idx, "completedAt", evt.timestamp);
        }
        break;
      }
      case "task_cancelled": {
        const p = evt.payload;
        const idx = tasks.findIndex(t => t.id === p.taskId);
        if (idx >= 0) {
          setTasks(idx, "status", "Cancelled");
          setTasks(idx, "progress", 0);
          setTasks(idx, "completedAt", evt.timestamp);
        }
        break;
      }
    }
  });

  onCleanup(() => unsub());

  const queuedTasks = createMemo(() => tasks.filter(t => t.status === "Pending" || t.status === "Running"));
  const doneTasks = createMemo(() => tasks.filter(t => t.status === "Completed" || t.status === "Failed" || t.status === "Cancelled").slice(0, 50));

  function itemInfo(key: string) {
    return props.resolver.getItem(key);
  }

  function typeLabel(task: CraftingTaskInfo): string {
    switch (task.taskType) {
      case "Transfer": return t("TASK_MOVE");
      default: return t("TASK_CRAFT");
    }
  }

  async function handleCancel(e: MouseEvent, taskId: string) {
    e.stopPropagation();
    try { await inv.cancelTask(taskId); } catch {}
  }

  function showTaskInfo(task: CraftingTaskInfo) {
    const item = itemInfo(task.itemKey);
    const imgSrc = item ? `/mc/minecraft_${item.name}_${item.metadata}.png` : "";

    const lines: string[] = [];
    if (item) lines.push(`<img src="${imgSrc}" style="width:24px;height:24px;image-rendering:pixelated;vertical-align:middle;margin-right:6px" /><strong>${item.displayName}</strong> x${task.quantity}`);
    else lines.push(`<strong>${task.itemKey}</strong> x${task.quantity}`);

    lines.push(`<br/><strong>${t("TASK_TYPE")}:</strong> ${typeLabel(task)}`);
    lines.push(`<strong>${t("TASK_STATUS")}:</strong> ${task.status}`);
    if (task.username) lines.push(`<strong>${t("TASK_STARTED_BY")}:</strong> ${task.username}`);
    if (task.sourceStorageName) lines.push(`<strong>${t("TASK_SOURCE")}:</strong> ${task.sourceStorageName}`);
    if (task.resultStorageName) lines.push(`<strong>${t("TASK_DESTINATION")}:</strong> ${task.resultStorageName}`);
    lines.push(`<strong>${t("TASK_CREATED")}:</strong> ${formatDateTime(task.createdAt)}`);
    if (task.completedAt) lines.push(`<strong>${t("TASK_FINISHED")}:</strong> ${formatDateTime(task.completedAt)}`);
    if (task.errorMessage) {
      lines.push(`<br/><span style="color:#f44336"><strong>${t("TASK_ERROR")}:</strong> ${task.errorMessage}</span>`);
      if (task.errorDetail) {
        lines.push(`<span style="color:#aaa;font-size:13px">${task.errorDetail}</span>`);
      }
    }

    showDialog({
      title: t("TASK_INFO_TITLE", typeLabel(task)),
      description: lines.join("<br/>"),
    });
  }

  return (
    <div class={styles.dock}>
      <Show when={open()}>
      <div class={styles.panel}>
        <div class={styles.title}>
          <i class="fa-solid fa-robot" />
          <Text val="TASK_BOT_TASKS" />
          <button class={styles.closeBtn} onClick={() => setOpen(false)} title={t("CLOSE")}>
            <i class="fa-solid fa-xmark" />
          </button>
        </div>

      <Show when={queuedTasks().length > 0}>
        <div class={styles.section}>
          <div class={styles.sectionTitle}><Text val="TASK_QUEUE" /></div>
          <For each={queuedTasks()}>
            {(task) => {
              const item = () => itemInfo(task.itemKey);
              return (
                <div class={`${styles.taskRow} ${task.status === "Pending" ? styles.taskPending : ""}`} onClick={() => showTaskInfo(task)}>
                  <div class={styles.taskHeader}>
                    <Show when={item()}>
                      {(it) => <ItemStackView stack={fromItemDef(it())} size={24} bare showCount={false} />}
                    </Show>
                    <div class={styles.taskInfo}>
                      <div class={styles.taskName}>{typeLabel(task)} {itemName(task.itemKey)} x{task.quantity}</div>
                      <div class={styles.taskStatus}>
                        {task.status === "Pending" ? <Text val="TASK_WAITING" /> : <Text val="TASK_RUNNING" />}
                      </div>
                    </div>
                    <button class={styles.cancelBtn} onClick={(e) => handleCancel(e, task.id)} title={t("CANCEL")}>
                      <i class="fa-solid fa-xmark" />
                    </button>
                  </div>
                  <Show when={task.status === "Running"}>
                    <div class={styles.progressBar}>
                      <div class={styles.progressFill} style={{ width: `${task.progress}%` }} />
                    </div>
                  </Show>
                  <Show when={task.status === "Pending"}>
                    <div class={styles.progressBar}>
                      <div class={styles.progressWaiting} />
                    </div>
                  </Show>
                </div>
              );
            }}
          </For>
        </div>
      </Show>

      <Show when={doneTasks().length > 0}>
        <div class={styles.section}>
          <div class={styles.sectionTitle}><Text val="TASK_RECENT" /></div>
          <For each={doneTasks()}>
            {(task) => {
              const item = () => itemInfo(task.itemKey);
              const statusClass = () => {
                if (task.status === "Failed") return styles.taskFailed;
                if (task.status === "Cancelled") return styles.taskCancelled;
                return styles.taskDone;
              };
              const statusLabel = () => {
                if (task.status === "Failed") return task.errorMessage ?? <Text val="TASK_ERROR" />;
                if (task.status === "Cancelled") return <Text val="TASK_CANCELLED" />;
                return <Text val="TASK_DONE" />;
              };
              const statusColor = () => {
                if (task.status === "Failed") return styles.statusFailed;
                if (task.status === "Cancelled") return styles.statusCancelled;
                return styles.statusDone;
              };
              return (
                <div class={`${styles.taskRow} ${statusClass()}`} onClick={() => showTaskInfo(task)}>
                  <div class={styles.taskHeader}>
                    <Show when={item()}>
                      {(it) => <ItemStackView stack={fromItemDef(it())} size={24} bare showCount={false} />}
                    </Show>
                    <div class={styles.taskInfo}>
                      <div class={styles.taskName}>{typeLabel(task)} {itemName(task.itemKey)} x{task.quantity}</div>
                      <div class={`${styles.taskStatus} ${statusColor()}`}>{statusLabel()}</div>
                    </div>
                  </div>
                </div>
              );
            }}
          </For>
        </div>
      </Show>

      <Show when={queuedTasks().length === 0 && doneTasks().length === 0}>
        <div class={styles.empty}><Text val="TASK_NO_TASKS" /></div>
      </Show>
      </div>
      </Show>

      <button class={styles.dockToggle} onClick={() => setOpen(!open())} title={t("TASK_BOT_TASKS")}>
        <i class="fa-solid fa-robot" />
        <Show when={queuedTasks().length > 0}>
          <span class={styles.dockBadge}>{queuedTasks().length}</span>
        </Show>
      </button>
    </div>
  );
}
